/// <reference lib="dom" />

import { z } from 'zod';
import type { SafeParseResult } from './zod-utils';
import { NestJsExceptionResponseSchema } from './schemas/common.schemas';

export interface HttpRequestHeaders {
    readonly [headerName: string]: string;
}

/*
 * This uses the global Fetch `RequestInit` for cross-runtime compatibility. In browsers it comes from `lib.dom`;
 * in Node it comes from `@types/node` (backed by undici) as a type-only dependency. We narrow only `headers`
 * to a string map to keep the API stable and serializable across runtimes.
 */
export interface FetchRequestInit extends Omit<RequestInit, 'headers'> {
    readonly headers?: HttpRequestHeaders;
}

export type FetchImplementation = (
    input: string | URL,
    init?: FetchRequestInit
) => Promise<Response>;

type FetchArguments = [input: string | URL, init?: FetchRequestInit];

export interface PillarApiClientOptions {
    readonly onSessionExpired?: () => void;
    readonly baseUrl?: string;
    readonly fetchImplementation?: FetchImplementation;
    readonly getRequestHeaders?: () => HttpRequestHeaders;
    readonly enableIdempotencyHeaders?: boolean;
    readonly onResponse?: (response: Response) => void;
    readonly onTraceContext?: (traceHeaderValue: string) => void;
    readonly onValidationError?: (error: ApiValidationError) => void;
}

export interface PillarApiRequestOptions {
    readonly method?: string;
    readonly body?: unknown;
    readonly headers?: HttpRequestHeaders;
    readonly handle401AsSessionExpired?: boolean;
    readonly acceptedStatusCodes?: ReadonlyArray<number>;
    readonly idempotencyKey?: string | null;
}

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        message: string,
        public readonly body?: unknown
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

export interface ApiResponseValidationError {
    readonly kind: 'response-body';
    readonly endpoint: string;
    readonly issues: z.core.$ZodIssue[];
    readonly response: unknown;
}

export interface ApiTraceHeaderValidationError {
    readonly kind: 'trace-header';
    readonly headerValue: string;
    readonly issues: z.core.$ZodIssue[];
}

export type ApiValidationError = ApiResponseValidationError | ApiTraceHeaderValidationError;

export interface PillarApiRequestExecutor {
    readonly baseUrl: string;
    requestWithSchema<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options?: PillarApiRequestOptions
    ): Promise<T>;
}

function isFormDataBody(value: unknown): value is FormData {
    return value instanceof FormData;
}

function isMutatingHttpMethod(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}

function generateIdempotencyKey(): string {
    const cryptoObject = globalThis.crypto;
    const randomUuidParseResult = z
        .function({
            input: [],
            output: z.string(),
        })
        .safeParse(cryptoObject.randomUUID.bind(cryptoObject));
    if (randomUuidParseResult.success) {
        return randomUuidParseResult.data();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isJsonContentType(contentType: string | null): boolean {
    if (contentType === null) {
        return false;
    }
    const normalized = contentType.toLowerCase().trim();
    return normalized.startsWith('application/json') || normalized.startsWith('application/json;');
}

function normalizeWhitespace(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
}

function stripHtml(value: string): string {
    const withoutComments: string = value.replace(/<!--[\s\S]*?-->/g, ' ');
    const withoutTags: string = withoutComments.replace(/<[^>]+>/g, ' ');
    return normalizeWhitespace(withoutTags);
}

function containsHtml(value: string): boolean {
    return /<!doctype\s+html|<html[\s>]|<\/?[a-z][\s\S]*?>/i.test(value);
}

function getFriendlyServerErrorMessage(status: number, statusText: string): string {
    if (status === 502) {
        return 'Temporary upstream network error. Please retry.';
    }
    if (status === 503) {
        return 'Service temporarily unavailable. Please retry.';
    }
    if (status === 504) {
        return 'Gateway timeout while contacting the server. Please retry.';
    }
    return `Server error (HTTP ${status}: ${statusText}). Please retry.`;
}

function extractErrorMessage(
    responseBody: string,
    response: Response,
    status: number,
    statusText: string
): string {
    if (!responseBody || responseBody.trim() === '') {
        return `HTTP ${status}: ${statusText}`;
    }

    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        if (status >= 500 && status < 600) {
            const plainTextResponseBody: string = stripHtml(responseBody);
            if (containsHtml(responseBody) || plainTextResponseBody.length === 0) {
                return getFriendlyServerErrorMessage(status, statusText);
            }
        }
        return responseBody;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(responseBody);
    } catch {
        return responseBody;
    }

    const result = NestJsExceptionResponseSchema.safeParse(parsed);
    if (result.success) {
        const validationErrors = result.data.errors;
        if (validationErrors !== undefined && validationErrors.length > 0) {
            const validationErrorSummaries: ReadonlyArray<string> = validationErrors.map(
                (validationError): string => {
                    const validationPath: string =
                        validationError.path !== undefined ? validationError.path : '(unknown path)';
                    const validationMessage: string =
                        validationError.message !== undefined
                            ? validationError.message
                            : 'validation error';
                    return `${validationPath}: ${validationMessage}`;
                }
            );
            const baseMessageParseResult = z.string().safeParse(result.data.message);
            const baseMessage: string = baseMessageParseResult.success
                ? baseMessageParseResult.data
                : 'Validation failed';
            return `${baseMessage} (${validationErrorSummaries.join('; ')})`;
        }
        const messageParseResult = z.string().safeParse(result.data.message);
        if (messageParseResult.success) {
            return messageParseResult.data;
        }
        const messageListParseResult = z.array(z.string()).safeParse(result.data.message);
        if (messageListParseResult.success) {
            return messageListParseResult.data.join(', ');
        }
    }

    return JSON.stringify(parsed);
}

function parseErrorBody(responseBody: string, response: Response): unknown {
    if (!responseBody || responseBody.trim() === '') {
        return undefined;
    }
    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        return undefined;
    }
    try {
        return JSON.parse(responseBody);
    } catch {
        return undefined;
    }
}

export function dedupe<ArgumentsTuple extends readonly unknown[], ReturnValue>(
    functionToDedupe: (...args: ArgumentsTuple) => Promise<ReturnValue>
): (...args: ArgumentsTuple) => Promise<ReturnValue> {
    const cacheBySerializedArguments = new Map<string, Promise<ReturnValue>>();

    return async (...args: ArgumentsTuple): Promise<ReturnValue> => {
        const serializedArgumentsKey = JSON.stringify(args);
        const existingPromise = cacheBySerializedArguments.get(serializedArgumentsKey);
        if (existingPromise !== undefined) {
            return existingPromise;
        }

        const dedupedPromise = functionToDedupe(...args);
        cacheBySerializedArguments.set(serializedArgumentsKey, dedupedPromise);
        return await dedupedPromise.finally(() => {
            cacheBySerializedArguments.delete(serializedArgumentsKey);
        });
    };
}

export function createPillarApiRequestExecutor(
    options: PillarApiClientOptions
): PillarApiRequestExecutor {
    const baseUrl: string = options.baseUrl ?? '/api';
    const onSessionExpired: (() => void) | undefined = options.onSessionExpired;
    const fetchImplementation: FetchImplementation =
        options.fetchImplementation ??
        ((...args: FetchArguments) => {
            return globalThis.fetch(...args);
        });
    const getRequestHeaders: (() => HttpRequestHeaders) | undefined = options.getRequestHeaders;
    const enableIdempotencyHeaders: boolean = options.enableIdempotencyHeaders ?? false;
    const onResponse: ((response: Response) => void) | undefined = options.onResponse;
    const onTraceContext: ((traceHeaderValue: string) => void) | undefined = options.onTraceContext;
    const onValidationError: ((error: ApiValidationError) => void) | undefined =
        options.onValidationError;
    const traceHeaderSchema: z.ZodType<string> = z.string().min(1);
    const httpPermanentRedirects: Record<string, URL> = {};

    function resolveBaseUrlForRequest(): string {
        let baseUrlForResolution: string = baseUrl;
        if (baseUrlForResolution.startsWith('/')) {
            // eslint-disable-next-line no-restricted-syntax
            const locationValue: unknown = (globalThis as unknown as Record<string, unknown>)
                .location;
            const locationParseResult = z
                .object({ origin: z.string().min(1) })
                .passthrough()
                .safeParse(locationValue);
            if (!locationParseResult.success) {
                throw new Error(
                    `Invalid baseUrl "${baseUrlForResolution}": relative baseUrl requires globalThis.location.origin`
                );
            }
            baseUrlForResolution = `${locationParseResult.data.origin}${baseUrlForResolution}`;
        } else if (
            !baseUrlForResolution.startsWith('https://') &&
            !baseUrlForResolution.startsWith('http://')
        ) {
            throw new Error(
                `Invalid baseUrl "${baseUrlForResolution}": must start with "https://", "http://", or "/"`
            );
        }

        return baseUrlForResolution;
    }

    function buildRequestUrl(endpoint: string): URL {
        const baseUrlForResolution: string = resolveBaseUrlForRequest();
        const fullPath: string = endpoint.startsWith('/')
            ? `${baseUrlForResolution}${endpoint}`
            : `${baseUrlForResolution}/${endpoint}`;
        return new URL(fullPath);
    }

    async function fetchPillarApi<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: PillarApiRequestOptions = {}
    ): Promise<T> {
        const defaultHeaders: HttpRequestHeaders = getRequestHeaders ? getRequestHeaders() : {};
        const {
            method = 'GET',
            body,
            headers = {},
            handle401AsSessionExpired = true,
            acceptedStatusCodes = [],
            idempotencyKey,
        } = options;
        const normalizedMethod = method.toUpperCase();
        const locationHeaderSchema: z.ZodType<string> = z.string().min(1);
        const isMultipartFormDataBody = isFormDataBody(body);
        const requestHeaders: Record<string, string> = {
            Accept: 'application/json',
            ...defaultHeaders,
            ...headers,
        };
        if (
            enableIdempotencyHeaders &&
            isMutatingHttpMethod(normalizedMethod) &&
            idempotencyKey !== null
        ) {
            requestHeaders['Idempotency-Key'] = idempotencyKey ?? generateIdempotencyKey();
        }
        if (!isMultipartFormDataBody) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        const requestInit: FetchRequestInit = {
            method: normalizedMethod,
            headers: requestHeaders,
            body:
                body === undefined
                    ? undefined
                    : isMultipartFormDataBody
                      ? body
                      : JSON.stringify(body),
            credentials: 'include',
        };

        const requestUrlAsURL: URL = buildRequestUrl(endpoint);
        const requestUrl: string = requestUrlAsURL.toString();
        let currentUrlAsURL: URL = httpPermanentRedirects[requestUrl] ?? requestUrlAsURL;
        let response: Response | null = null;
        const maxRedirects = 3;
        let redirectIsTransient: boolean = false;
        for (let redirectCount = 0; redirectCount <= maxRedirects; redirectCount += 1) {
            response = await fetchImplementation(currentUrlAsURL, requestInit);
            if (onResponse) {
                onResponse(response);
            }
            if (response.status >= 300 && response.status < 400) {
                await response.text();
                const locationHeaderValue: string | null = response.headers.get('location');
                if (locationHeaderValue === null) {
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Redirect response missing Location header'
                    );
                }
                const locationParseResult = locationHeaderSchema.safeParse(locationHeaderValue);
                if (!locationParseResult.success) {
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Redirect response has invalid Location header'
                    );
                }
                currentUrlAsURL = new URL(locationParseResult.data, currentUrlAsURL);
                redirectIsTransient = redirectIsTransient || ![301, 308].includes(response.status);
                if (!redirectIsTransient) {
                    httpPermanentRedirects[requestUrl] = currentUrlAsURL;
                }
                continue;
            }
            if (response.status >= 400) {
                delete httpPermanentRedirects[requestUrl];
            }
            break;
        }
        if (response === null) {
            throw new ApiError(500, 'Invalid Response', 'No response received from server');
        }

        const traceHeaderValue: string | null = response.headers.get('x-cloud-trace-context');
        if (traceHeaderValue !== null) {
            const traceHeaderParseResult: SafeParseResult<string> =
                traceHeaderSchema.safeParse(traceHeaderValue);
            if (!traceHeaderParseResult.success) {
                const validationError: ApiTraceHeaderValidationError = {
                    kind: 'trace-header',
                    headerValue: traceHeaderValue,
                    issues: traceHeaderParseResult.error.issues,
                };
                if (onValidationError) {
                    onValidationError(validationError);
                }
                throw new ApiError(500, 'Invalid Response', 'Invalid X-Cloud-Trace-Context header');
            }
            if (onTraceContext) {
                onTraceContext(traceHeaderParseResult.data);
            }
        }

        const responseBody = await response.text();

        if (response.status === 401) {
            if (onSessionExpired && handle401AsSessionExpired) {
                onSessionExpired();
            }
            const errorBody: unknown = parseErrorBody(responseBody, response);
            const errorMessage = extractErrorMessage(
                responseBody,
                response,
                response.status,
                response.statusText
            );
            throw new ApiError(response.status, response.statusText, errorMessage, errorBody);
        }

        if (!response.ok && !acceptedStatusCodes.includes(response.status)) {
            const errorBody: unknown = parseErrorBody(responseBody, response);
            const errorMessage = extractErrorMessage(
                responseBody,
                response,
                response.status,
                response.statusText
            );
            throw new ApiError(response.status, response.statusText, errorMessage, errorBody);
        }

        if (response.status === 204) {
            return schema.parse(undefined);
        }

        if (!responseBody || responseBody.trim() === '') {
            throw new ApiError(response.status, response.statusText, 'Empty response body');
        }

        const contentType: string | null = response.headers.get('content-type');
        if (!isJsonContentType(contentType)) {
            throw new ApiError(
                response.status,
                response.statusText,
                `Expected JSON response but got Content-Type: ${contentType ?? 'missing'}`
            );
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(responseBody);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            throw new ApiError(
                response.status,
                response.statusText,
                `Failed to parse JSON response: ${errorMessage}`
            );
        }

        try {
            return schema.parse(parsed);
        } catch (validationError: unknown) {
            if (validationError instanceof z.ZodError) {
                const validationErrorDetails: ApiResponseValidationError = {
                    kind: 'response-body',
                    endpoint,
                    issues: validationError.issues,
                    response: parsed,
                };
                if (onValidationError) {
                    onValidationError(validationErrorDetails);
                }
                throw new ApiError(
                    500,
                    'Invalid Response',
                    `API response validation failed: ${validationError.issues
                        .map((issue: z.core.$ZodIssue) => issue.message)
                        .join(', ')}`
                );
            }
            throw validationError;
        }
    }

    return {
        baseUrl,
        requestWithSchema: fetchPillarApi,
    };
}
